import moment from 'moment';
import { ListOption } from '@/components/list-item';
import {
  OrderFeeDisplayType,
  OrderHistory_OrdersQuery,
  OrderHistoryListOrderFieldsFragment,
  OrderHistoryListOrderWithFeesFieldsFragment,
  OrderInvoiceFieldsFragment,
  Orders_CreditRequestBatchFieldsFragment,
  Orders_GetOrderFieldsFragment,
} from '@/types';

interface OrderFee {
  amount_at_invoice_micros?: number | null;
  amount_at_order_micros?: number | null;
  display_type: OrderFeeDisplayType;
  name: string;
}

// OrderHistoryList may consist of either Orders or OrderInvoices
// We refer to orders as "Pepper Orders" and invoices as "External Orders"
export interface PepperOrderListOption {
  order: OrderHistoryListOrderFieldsFragment;
  orderFees: OrderFee[];
}
export interface ErpOrderListOption {
  invoice: OrderInvoiceFieldsFragment;
}
export interface CreditRequestBatchListOption {
  creditRequest: Orders_CreditRequestBatchFieldsFragment;
}
export type OrderHistoryListOption = ListOption<
  PepperOrderListOption | ErpOrderListOption | CreditRequestBatchListOption
>;

// Helper function to determine if a date is in the past
export const isPastDate = (dateString: string): boolean => {
  const today = moment().startOf('day');
  const inputDate = moment(dateString);
  return inputDate.isBefore(today);
};

// Helper function to determine if a date is today or in the past
export const isPastDateOrToday = (dateString: string): boolean => {
  const today = moment().startOf('day');
  const inputDate = moment(dateString);
  return inputDate.isSameOrBefore(today);
};

// Helper function to extract the order date
export const getOrderDate = (
  item:
    | OrderHistoryListOrderFieldsFragment
    | OrderInvoiceFieldsFragment
    | Orders_GetOrderFieldsFragment,
): string => {
  return 'invoice_date' in item
    ? item.invoice_date
    : item.order_tracking?.order_completed_delivery_datetime ||
        item.supplier_accepted_delivery_time ||
        item.restaurant_desired_delivery_time ||
        '';
};

// Create sections for orders/invoices based on whether they are past or upcoming
export const createSectionData = (
  items: OrderHistory_OrdersQuery['orders'] | OrderInvoiceFieldsFragment[],
  isPast: boolean,
): OrderHistoryListOption[] =>
  items.reduce<OrderHistoryListOption[]>((acc, item) => {
    const date = getOrderDate(item);
    if (isPastDate(date) === isPast) {
      acc.push({
        metadata:
          'invoice_date' in item
            ? { invoice: item }
            : { order: item, orderFees: item.order_fees as OrderFee[] },
        title: item.uuid,
        value: item.uuid,
      });
    }
    return acc;
  }, []);

// Create sections for orders/invoices based on whether they are past or upcoming
export const createSearchOrderHistorySectionData = (
  items:
    | OrderHistoryListOrderWithFeesFieldsFragment[]
    | OrderInvoiceFieldsFragment[],
): OrderHistoryListOption[] =>
  items.reduce<OrderHistoryListOption[]>((acc, item) => {
    if ('invoice_date' in item) {
      acc.push({
        metadata: { invoice: item },
        title: item.uuid,
        value: item.uuid,
      });
    } else if (item.order) {
      acc.push({
        metadata: {
          order: item.order,
          orderFees: item.order_fees,
        },
        title: item.order.uuid,
        value: item.order.uuid,
      });
    }

    return acc;
  }, []);
